import * as React from 'react';
import propz from 'propz';
import { Component } from 'react';
import { AppUser } from 'Src/views/App/App';
import { Participant, SchoolEvent, SchoolEventIndividualData, SchoolEventTeamDataPlayer } from 'Src/models/event';
import {
  getAllSchoolIdsFromEvent,
  getEventParticipants,
  getParticipantImg,
  getParticipantName,
  getParticipantText,
  isEventStatusAccepted,
  isEventStatusFinished,
  isClubEvent,
  isHousesEvent,
  isInternalEvent,
  isInterSchoolsEvent,
  isMultipartyEvent,
  isTeamOrTwoOnTwoSportEvent,
  getHouseColors
} from 'Src/helpers/event/event';
import { DEFAULT_LIMIT, DEFAULT_SKIP } from 'Src/consts/table';
import { Loader } from 'Src/components/Loader/Loader';
import { SchoolEventTeamPlayers } from './SchoolEventTeamPlayers';
import { SchoolEventIndividualPlayers } from './SchoolEventIndividualPlayers';
import { History } from 'history';
import { EVENT_STATUS } from '../../../../../../consts/event';
import { getSchoolsPublic } from '../../../../../../helpers/service/public/public';
import { isInviteStatusAccepted } from '../../../../../../helpers/invite/invite';
import { PencilEditIcon } from '../../../../../../components/PencilEditIcon/PencilEditIcon';
import { RemoveIcon } from '../../../../../../components/RemoveIcon/RemoveIcon';
import { isCricket } from '../../../../../../helpers/sport/cricket';
import { SchoolEventCricketResult } from './SchoolEventCricketResult';
import { ActionItem, ActionsButton } from '../../../../../../components/ActionsButton/ActionsButton';
import {
  isEventStatusCanceled,
  isEventStatusInvitesSent,
  isTournamentEvent,
  isTournamentEventReadOnly
} from '../../../../../../helpers/event/event';
import { getEventKitsForAllSchools } from 'Src/helpers/service/admin/schoolEvents';
import { KitLabBackendKit } from 'Src/helpers/service/admin/kitLab';
import { KitPatternVariant, KitSkortPattern } from 'Src/types/kitLab';
import { KitShirtPreview } from 'Src/components/KitLab/KitShirtPreview';
import { KitShortsPreview } from 'Src/components/KitLab/KitShortsPreview';
import { KitSkortPreview } from 'Src/components/KitLab/KitSkortPreview';
import { KitBottomPattern } from 'Src/types/kitLab';
import { SchoolEventPlayerSummary } from 'Src/components/SchoolEventPlayersSummary/SchoolEventPlayersSummary';
import './SchoolEventTeamsTab.scss';
import { ParticipantResultBadge } from 'Src/components/ParticipantResultBadge/ParticipantResultBadge';
import { Report } from 'Src/models/report';
import { AvailabilityStatusLegend } from 'Src/components/AvailabilityStatusDot/AvailabilityStatusDot';

interface State {
  isLoading: boolean;
  isKitsLoading: boolean;
  eventDetailsBySchool: { [schoolId: string]: any };
  allKits: KitLabBackendKit[];
}

interface Props {
  user: AppUser;
  history: History;
  eventInitial: SchoolEvent;
  participants: Participant[];
  playersForSummary?: SchoolEventPlayerSummary[];
  availabilityReports?: Report[];
  onRemoveParticipantClick: (participant: Participant, isOneParticipant: boolean) => void;
  canRemoveParticipants?: boolean;
  canEditAllParticipants?: boolean;
  onEditParticipantClick?: (participant: Participant) => void;
  onChangeSchoolClick: (participant: Participant) => void;
  onChangeIsCaptainPlayer?: (event, playerId: string) => void;
  onChangeIsViceCaptainPlayer?: (event, playerId: string) => void;
  onChangeIsSubPlayer?: (event, playerId: string) => void;
}

function isSkortPatternId(pattern?: KitBottomPattern | null): boolean {
  if (!pattern) return false;
  return (
    pattern === 'SKORT_PLAIN' ||
    pattern === 'SKORT_VERTICAL_STRIPES' ||
    pattern === 'SKORT_SIDE' ||
    pattern === 'SKORT_TRIANGLES' ||
    pattern === 'SKORT_BOTTOM_TRIANGLES' ||
    pattern === 'SKORT_SEMICIRCLES'
  );
}

function mapBottomToSkortPattern(pattern: KitBottomPattern | undefined): KitSkortPattern {
  switch (pattern) {
    case 'SKORT_VERTICAL_STRIPES':
      return 'verticalStripes';
    case 'SKORT_SIDE':
      return 'sideStripes';
    case 'SKORT_TRIANGLES':
      return 'sideTriangles';
    case 'SKORT_BOTTOM_TRIANGLES':
      return 'sideBottomTriangles';
    case 'SKORT_SEMICIRCLES':
      return 'sideSemiCircles';
    case 'SKORT_PLAIN':
    default:
      return 'plain';
  }
}

type ParticipantsStats = {
  participantsBySchoolId: { [schoolId: string]: number };
  activeSchoolParticipantsCount: number;
  opponentSchoolParticipantsCount: number;
};

export class SchoolEventTeamsTab extends Component<Props, State> {
  private participantScrollRef = React.createRef<HTMLDivElement>();

  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      isKitsLoading: false,
      eventDetailsBySchool: {},
      allKits: []
    };
  }

  componentDidMount() {
    this.loadKitsForEvent();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.eventInitial.id !== this.props.eventInitial.id) {
      this.loadKitsForEvent();
    }
  }

  scrollParticipants = (direction: -1 | 1): void => {
    const container = this.participantScrollRef.current;
    if (!container) return;

    container.scrollBy({
      left: direction * Math.max(320, container.clientWidth * 0.72),
      behavior: 'smooth'
    });
  };

  private kitForParticipantCache = new Map<string, KitLabBackendKit | null>();
  private kitPreviewLayoutCache = new Map<
    string,
    {
      shirtPattern: KitPatternVariant;
      primaryColor: string;
      secondaryColor: string;
      bottomIsSkort: boolean;
      shortsVariant: 'plain' | 'sideStripes' | 'sideTriangles' | 'sideBigTriangles' | 'sideSemiCircles';
      skortPattern: KitSkortPattern;
      shortsPrimaryColor: string;
      shortsSecondaryColor: string;
    }
  >();

  private loadKitsForEvent = async () => {
    const { user, eventInitial } = this.props;
    const eventId = eventInitial.id;

    if (!eventId) return;
    this.kitForParticipantCache.clear();
    this.kitPreviewLayoutCache.clear();

    this.setState({ isKitsLoading: true });

    try {
      const response = await getEventKitsForAllSchools(user, eventId);

      if (!response) {
        this.setState({ isKitsLoading: false });
        return;
      }

      const { mappings, kits } = response;

      const eventDetailsBySchool: { [schoolId: string]: any } = {};

      (mappings || []).forEach(item => {
        if (!item) return;
        const sid = propz.get(item, ['schoolId'], null);
        const key = sid ? sid.toString() : 'default';
        eventDetailsBySchool[key] = item;
      });

      this.setState({
        eventDetailsBySchool,
        allKits: kits || [],
        isKitsLoading: false
      });
    } catch (e) {
      console.error('Failed to load kits for teams tab', e);
      this.setState({ isKitsLoading: false });
    }
  };

  getSearch(participant: Participant): string {
    const { eventInitial } = this.props;
    const { id: eventId } = eventInitial;
    let search = [`event=${eventId}`];

    const { team, house } = participant;

    const isTeamExist = typeof team !== 'undefined';
    if (isTeamExist) {
      search = [...search, `team=${team.id}`];
    }

    const isHouseExist = typeof house !== 'undefined';
    if (isHouseExist) {
      search = [...search, `house=${house.id}`];
    }

    return search.join('&');
  }

  getSearchForUpdateTeam(participant: Participant): string {
    const { eventInitial } = this.props;
    const { id: eventId } = eventInitial;

    const teamSourceOfCloneId = propz.get(participant, ['team', 'cloneOf']);
    const teamId = propz.get(participant, ['team', 'id']);

    let search = [`event=${eventId}`, `clone=${teamSourceOfCloneId}`, `team=${teamId}`];
    return search.join('&');
  }

  onEditParticipantClick = (participant: Participant): void => {
    const { history, eventInitial, onEditParticipantClick } = this.props;

    if (onEditParticipantClick) {
      onEditParticipantClick(participant);
      return;
    }

    const search = this.getSearch(participant);

    history.push({
      pathname: isTeamOrTwoOnTwoSportEvent(eventInitial) ? 'teamManager' : 'individualManager',
      search: search
    });
  };

  getActionItems(participant: Participant, stats: ParticipantsStats): ActionItem[] {
    const {
      eventInitial,
      user,
      onChangeSchoolClick,
      onRemoveParticipantClick,
      history,
      canRemoveParticipants = true,
      canEditAllParticipants = false
    } = this.props;
    const { activeSchoolId } = user;

    const activeSchoolParticipantsCount = stats.activeSchoolParticipantsCount;
    const opponentSchoolParticipantsCount = stats.opponentSchoolParticipantsCount;

    const { inviterSchoolId } = eventInitial;

    const participantSchoolId = propz.get(participant, ['school', 'id'], '');
    const isActiveSchoolParticipant = activeSchoolId === participantSchoolId;

    const interSchoolsConditionForEditButton = isInterSchoolsEvent(eventInitial) && isActiveSchoolParticipant;

    const isActiveEditButton =
      canEditAllParticipants ||
      isInternalEvent(eventInitial) ||
      isHousesEvent(eventInitial) ||
      interSchoolsConditionForEditButton;

    const isOneParticipant = (stats.participantsBySchoolId[participantSchoolId] || 0) === 1;

    const count = isHousesEvent(eventInitial) || isInternalEvent(eventInitial) ? 2 : 1;

    const isActiveSchoolInviter = activeSchoolId === inviterSchoolId;

    const activeSchoolConditionForRemoveButton = isActiveSchoolParticipant && activeSchoolParticipantsCount > count;
    const opponentSchoolsConditionForRemoveButton =
      !isActiveSchoolParticipant && opponentSchoolParticipantsCount > 1 && isActiveSchoolInviter;

    const isActiveRemoveButton =
      canRemoveParticipants && (activeSchoolConditionForRemoveButton || opponentSchoolsConditionForRemoveButton);

    const isAwaitingOpponent = getParticipantText(eventInitial, participant, user) === 'Awaiting opponent';
    const isActiveChangeSchoolButton =
      isActiveSchoolInviter && (isEventStatusInvitesSent(eventInitial) || isAwaitingOpponent);

    const search = this.getSearch(participant);

    const isActiveChangeTeamButton = isTeamOrTwoOnTwoSportEvent(eventInitial) && isActiveSchoolParticipant;

    const teamSourceOfClone = propz.get(participant, ['team', 'cloneOf']);
    const isTeamSourseOfCloneExists = typeof teamSourceOfClone !== 'undefined' && teamSourceOfClone !== '';

    const isActiveUpdateTeamButton =
      isTeamOrTwoOnTwoSportEvent(eventInitial) && isActiveSchoolParticipant && isTeamSourseOfCloneExists;

    const tournamentOwnerSchoolId = propz.get(eventInitial, ['tournament', 'schoolId'], '');
    const isTournamentOrganiser = String(tournamentOwnerSchoolId) === String(activeSchoolId);

    if (isTournamentEvent(eventInitial) && isTournamentEventReadOnly(eventInitial) && !isTournamentOrganiser) {
      return [
        { itemText: 'Edit players', onItemClick: () => {}, isActive: false },
        { itemText: 'Swap with a team', onItemClick: () => {}, isActive: false },
        { itemText: 'Update team', onItemClick: () => {}, isActive: false },
        { itemText: 'Remove team', onItemClick: () => {}, isActive: false }
      ];
    }

    const actionItems =
      isInterSchoolsEvent(eventInitial) && !isActiveSchoolParticipant
        ? [
            {
              itemText: 'Edit players',
              onItemClick: () => this.onEditParticipantClick(participant),
              isActive: isActiveEditButton
            },
            {
              itemText: 'Change opponent',
              onItemClick: () => onChangeSchoolClick(participant),
              isActive: isActiveChangeSchoolButton
            },
            {
              itemText: 'Remove team',
              onItemClick: () => onRemoveParticipantClick(participant, isOneParticipant),
              isActive: isActiveRemoveButton
            }
          ]
        : [
            {
              itemText: 'Edit players',
              onItemClick: () => this.onEditParticipantClick(participant),
              isActive: isActiveEditButton
            },
            {
              itemText: 'Swap with a team',
              onItemClick: () => history.push({ pathname: 'existingTeamManager', search }),
              isActive: isActiveChangeTeamButton
            },
            {
              itemText: 'Update team',
              onItemClick: () =>
                history.push({ pathname: 'updatingTeamManager', search: this.getSearchForUpdateTeam(participant) }),
              isActive: isActiveUpdateTeamButton
            },
            {
              itemText: 'Remove team',
              onItemClick: () => onRemoveParticipantClick(participant, isOneParticipant),
              isActive: isActiveRemoveButton
            }
          ];

    return canRemoveParticipants ? actionItems : actionItems.filter(item => item.itemText !== 'Remove team');
  }

  private getKitPreviewLayout(kit: KitLabBackendKit) {
    const kitId = kit?._id ? kit._id.toString() : '';
    if (kitId && this.kitPreviewLayoutCache.has(kitId)) {
      return this.kitPreviewLayoutCache.get(kitId)!;
    }

    let primaryColor = '#1f78ff';
    let secondaryColor = '#ffd24d';
    let shortsPrimaryColor = primaryColor;
    let shortsSecondaryColor = secondaryColor;
    let shirtPattern: KitPatternVariant = 'plain';
    let bottomPattern: KitBottomPattern = 'SHORTS_PLAIN';

    if (kit.layoutPreset) {
      try {
        const parsed = JSON.parse(kit.layoutPreset as any) as any;
        if (parsed) {
          if (parsed.primaryColor) primaryColor = parsed.primaryColor;
          if (parsed.secondaryColor) secondaryColor = parsed.secondaryColor;
          if (parsed.shortsPrimaryColor) shortsPrimaryColor = parsed.shortsPrimaryColor;
          if (parsed.shortsSecondaryColor) shortsSecondaryColor = parsed.shortsSecondaryColor;
          if (parsed.shirtPattern) shirtPattern = parsed.shirtPattern as KitPatternVariant;
          if (parsed.bottomPattern) bottomPattern = parsed.bottomPattern as KitBottomPattern;
        }
      } catch (e) {}
    }

    if (!kit.layoutPreset && Array.isArray(kit.segments)) {
      const primarySeg = kit.segments.find((s: any) => s.segmentKey === 'SHIRT_PRIMARY');
      const secondarySeg = kit.segments.find((s: any) => s.segmentKey === 'SHIRT_SECONDARY');
      const shortsPrimarySeg = kit.segments.find((s: any) => s.segmentKey === 'SHORTS_PRIMARY');
      const shortsSecondarySeg = kit.segments.find((s: any) => s.segmentKey === 'SHORTS_SECONDARY');

      if (primarySeg?.color) primaryColor = primarySeg.color;
      if (secondarySeg?.color) secondaryColor = secondarySeg.color;
      if (shortsPrimarySeg?.color) shortsPrimaryColor = shortsPrimarySeg.color;
      if (shortsSecondarySeg?.color) shortsSecondaryColor = shortsSecondarySeg.color;
    }

    const bottomIsSkort = isSkortPatternId(bottomPattern);
    const skortPattern = bottomIsSkort ? mapBottomToSkortPattern(bottomPattern) : 'plain';

    let shortsVariant: 'plain' | 'sideStripes' | 'sideTriangles' | 'sideBigTriangles' | 'sideSemiCircles' = 'plain';
    if (!bottomIsSkort) {
      switch (bottomPattern) {
        case 'SHORTS_SIDE':
          shortsVariant = 'sideStripes';
          break;
        case 'SHORTS_TRIANGLES':
          shortsVariant = 'sideTriangles';
          break;
        case 'SHORTS_BIG_TRIANGLES':
          shortsVariant = 'sideBigTriangles';
          break;
        case 'SHORTS_SEMICIRCLES':
          shortsVariant = 'sideSemiCircles';
          break;
        default:
          shortsVariant = 'plain';
      }
    }

    const result = {
      shirtPattern,
      primaryColor,
      secondaryColor,
      bottomIsSkort,
      shortsVariant,
      skortPattern,
      shortsPrimaryColor,
      shortsSecondaryColor
    };

    if (kitId) this.kitPreviewLayoutCache.set(kitId, result);

    return result;
  }

  private getKitForParticipant(participant: Participant): KitLabBackendKit | null {
    const { eventDetailsBySchool, allKits } = this.state;

    if (!eventDetailsBySchool || !allKits || allKits.length === 0) return null;

    const participantSchoolId = propz.get(participant, ['school', 'id'], null);
    const schoolKey = participantSchoolId ? participantSchoolId.toString() : 'default';

    const teamId = propz.get(participant, ['team', 'id'], null);
    const teamKey = teamId ? teamId.toString() : '';

    const cacheKey = `${schoolKey}|${teamKey}`;
    if (this.kitForParticipantCache.has(cacheKey)) {
      return this.kitForParticipantCache.get(cacheKey)!;
    }

    const detailsForSchool = eventDetailsBySchool[schoolKey] || eventDetailsBySchool['default'];
    if (!detailsForSchool) {
      this.kitForParticipantCache.set(cacheKey, null);
      return null;
    }

    const mappings: any[] = propz.get(detailsForSchool, ['kitLabKits'], []) || [];
    if (!mappings.length) {
      this.kitForParticipantCache.set(cacheKey, null);
      return null;
    }

    let mapping: any = null;

    if (teamKey) {
      mapping =
        mappings.find(m => {
          const mTeamId = m.teamId ? m.teamId.toString() : null;
          return mTeamId === teamKey;
        }) || null;
    }

    if (!mapping) {
      mapping =
        mappings.find(m => {
          const hasTeam = !!m.teamId;
          return !hasTeam;
        }) || null;
    }

    if (!mapping || !mapping.kitId) {
      this.kitForParticipantCache.set(cacheKey, null);
      return null;
    }

    const kitIdStr = mapping.kitId.toString();
    const kit = allKits.find(k => k._id && k._id.toString() === kitIdStr) || null;

    this.kitForParticipantCache.set(cacheKey, kit);

    return kit;
  }

  render() {
    const {
      eventInitial,
      user,
      participants,
      playersForSummary,
      onChangeIsCaptainPlayer,
      onChangeIsViceCaptainPlayer,
      onChangeIsSubPlayer
    } = this.props;
    const activeSchoolId = user.activeSchoolId;

    const participantsBySchoolId: { [schoolId: string]: number } = {};
    let activeSchoolParticipantsCount = 0;
    let opponentSchoolParticipantsCount = 0;

    participants.forEach(p => {
      const sid = propz.get(p, ['school', 'id'], '');
      if (!sid) return;

      participantsBySchoolId[sid] = (participantsBySchoolId[sid] || 0) + 1;

      if (sid === activeSchoolId) activeSchoolParticipantsCount++;
      else opponentSchoolParticipantsCount++;
    });

    const stats: ParticipantsStats = {
      participantsBySchoolId,
      activeSchoolParticipantsCount,
      opponentSchoolParticipantsCount
    };

    const { isLoading } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const isCricketResultShow =
      isEventStatusFinished(eventInitial) && isCricket(eventInitial) && !isMultipartyEvent(eventInitial);

    return (
      <div className="eEventParticipants">
        {isCricketResultShow && <SchoolEventCricketResult event={eventInitial} user={user} />}
        <div className="eEventParticipants__heading">
          <div>
            <h2 className="eEventParticipants__title">Participants</h2>
            <div className="eEventParticipants__subtitle">
              {participants.length} {participants.length === 1 ? 'participant side' : 'participant sides'} in this event
            </div>
          </div>
          {participants.length > 1 && (
            <div className="eEventParticipants__scrollControls" aria-label="Scroll participants">
              <button
                type="button"
                className="eEventParticipants__scrollButton"
                aria-label="Previous participants"
                onClick={() => this.scrollParticipants(-1)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <button
                type="button"
                className="eEventParticipants__scrollButton"
                aria-label="Next participants"
                onClick={() => this.scrollParticipants(1)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          )}
        </div>
        <div className="eParticipantScroll" ref={this.participantScrollRef}>
          {participants.map((participant, index) => {
            const participantSchoolId = String(propz.get(participant, ['school', 'id'], ''));
            const isActiveSchoolParticipant = participantSchoolId === String(user.activeSchoolId);
            const participantAvailabilityReports = isActiveSchoolParticipant ? this.props.availabilityReports : undefined;
            const availabilitySelfService = propz.get(user, ['activeSchool', 'settings', 'availabilityWorkflow'], 'SELF_SERVICE') === 'SELF_SERVICE';
            const participantName = getParticipantName(eventInitial, participant);
            const participantText = getParticipantText(eventInitial, participant, user);
            const participantImg = getParticipantImg(participant);
            const teamPlayers = propz.get<SchoolEventTeamDataPlayer[]>(participant, ['teamPlayers'], []);
            const individualPlayers = propz.get<SchoolEventIndividualData[]>(participant, ['individualPlayers'], []);

            const actionItems = this.getActionItems(participant, stats);
            const manageParticipantsActionText =
              isTeamOrTwoOnTwoSportEvent(eventInitial) && !isClubEvent(eventInitial)
                ? 'Manage players'
                : 'Manage participants';
            const secondaryActions = actionItems.map(item =>
              item.itemText === 'Edit players' ? { ...item, itemText: manageParticipantsActionText } : item
            );
            const isEventCanceled = isEventStatusCanceled(eventInitial);

            const houseColors = getHouseColors(participant);

            const kit = this.getKitForParticipant(participant);
            const kitPreview = kit
              ? (() => {
                  const {
                    shirtPattern,
                    primaryColor,
                    secondaryColor,
                    bottomIsSkort,
                    shortsVariant,
                    skortPattern,
                    shortsPrimaryColor,
                    shortsSecondaryColor
                  } = this.getKitPreviewLayout(kit);

                  return (
                    <div className="eEventTeams__kitIcon">
                      <div className="eEventTeams__kitIcon-shirt">
                        <KitShirtPreview
                          variant={shirtPattern}
                          primaryColor={primaryColor}
                          secondaryColor={secondaryColor}
                          className="eKitLab__shirtSvg--mini"
                        />
                      </div>

                      <div className="eEventTeams__kitIcon-bottom">
                        {bottomIsSkort ? (
                          <KitSkortPreview
                            pattern={skortPattern}
                            primaryColor={shortsPrimaryColor}
                            secondaryColor={shortsSecondaryColor}
                            className="eKitLab__bottomSvg--mini eKitLab__bottomSvg--miniSkort"
                          />
                        ) : (
                          <KitShortsPreview
                            variant={shortsVariant}
                            primaryColor={shortsPrimaryColor}
                            secondaryColor={shortsSecondaryColor}
                            className="eKitLab__bottomSvg--mini"
                          />
                        )}
                      </div>
                    </div>
                  );
                })()
              : null;

            return (
              <div key={`participant_${index}`} className={'eParticipantGeneral'}>
                <div className="bParticipantSchoolLogoSections">
                  <div className="bViewOpponentActions">
                    {!isEventCanceled && secondaryActions.length > 0 && (
                      <ActionsButton
                        actionItems={secondaryActions}
                        text="…"
                        buttonClassName="btn eEventParticipants__more dropdown-toggle"
                      />
                    )}
                  </div>
                  <div className="eEventParticipants__identity">
                    <img className="eParticipantSchoolLogo" src={participantImg} alt="" />
                    <div className="eEventParticipants__identityCopy">
                      <div className="eParticipantNameRow">
                        <div className="eParticipantNameAndHouse">
                          <span className="eParticipantName">{participantName}</span>
                          {houseColors.length > 0 && (
                            <span className="house-colors" aria-label="House colours">
                              {houseColors.map((color, colorIndex) => (
                                <span key={colorIndex} style={{ backgroundColor: color }} />
                              ))}
                            </span>
                          )}
                        </div>
                        {kitPreview}
                      </div>
                      <div className="eEventParticipants__rosterCount">
                        {teamPlayers.length || individualPlayers.length}{' '}
                        {(teamPlayers.length || individualPlayers.length) === 1 ? 'student' : 'students'}
                      </div>
                    </div>
                  </div>
                  <div className="ePlayerScore">
                    <ParticipantResultBadge event={eventInitial} participant={participant} />
                  </div>
                </div>
                <div className="bParticipantSchoolPlayersSections">
                  {participantAvailabilityReports && <AvailabilityStatusLegend reports={participantAvailabilityReports} permissionIds={[...teamPlayers,...individualPlayers].map(player => String(player.permissionId))} selfService={availabilitySelfService} />}
                  {!participantText && (
                    <div className="eEventParticipants__rosterLabel">
                      {teamPlayers.length > 0 ? 'Squad' : 'Participants'}
                    </div>
                  )}
                  {participantText ? (
                    <div className="eParticipantText">{participantText}</div>
                  ) : (
                    <>
                      <SchoolEventTeamPlayers
                        players={teamPlayers}
                        event={eventInitial}
                        playersForSummary={playersForSummary}
                        availabilityReports={participantAvailabilityReports}
                        availabilitySelfService={availabilitySelfService}
                        onChangeIsCaptainPlayer={onChangeIsCaptainPlayer}
                        onChangeIsViceCaptainPlayer={onChangeIsViceCaptainPlayer}
                        onChangeIsSubPlayer={onChangeIsSubPlayer}
                      />
                      <SchoolEventIndividualPlayers players={individualPlayers} event={eventInitial} availabilityReports={participantAvailabilityReports} availabilitySelfService={availabilitySelfService} />
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}
